/**
 * ArtsSection — /arts/:section
 * Grid listing of every entity in one section, with category filter,
 * country filter, and pagination.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Filter, Star, PenSquare } from "lucide-react";
import { http } from "@/lib/api";

export default function ArtsSection() {
  const { section } = useParams();
  const [sp, setSp] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);

  const category = sp.get("category") || "";
  const country  = sp.get("country")  || "";
  const q        = sp.get("q")        || "";

  useEffect(() => {
    (async () => {
      try {
        const m = await http.get("/arts/meta").then(r => r.data);
        setMeta(m);
      } catch { setMeta(null); }
    })();
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ section, limit: "48" });
        if (category) params.set("category", category);
        if (country)  params.set("country",  country);
        if (q)        params.set("q",        q);
        const r = await http.get(`/arts/entities?${params}`).then(r => r.data);
        if (alive) setData(r);
      } catch {
        if (alive) setData({ total: 0, items: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [section, category, country, q]);

  const sectionDef = useMemo(() => (meta?.sections || []).find(s => s.slug === section), [meta, section]);
  const categories = useMemo(() => (meta?.section_categories?.[section] || []), [meta, section]);
  const setFilter = (key, val) => {
    const next = new URLSearchParams(sp);
    if (val) next.set(key, val); else next.delete(key);
    setSp(next);
  };

  if (!sectionDef && meta) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="font-display text-3xl mb-2">Section not found</h1>
        <p className="opacity-70 mb-4">The section <code>{section}</code> doesn't exist.</p>
        <Link to="/arts" className="wofy-btn-ghost"><ArrowLeft className="w-4 h-4" /> Back to Arts</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid={`arts-section-${section}`}>
      <div className="mb-6">
        <Link to="/arts" className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> Arts hub
        </Link>
        <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
          {sectionDef?.kind?.toUpperCase() || "SECTION"}
        </div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">{sectionDef?.label || section}</h1>
            {sectionDef?.tagline && <p className="opacity-80 mt-1">{sectionDef.tagline}</p>}
          </div>
          <Link to={`/arts/submit?section=${section}`} className="wofy-btn-primary" data-testid="arts-add-in-section">
            <PenSquare className="w-4 h-4" /> Add to {sectionDef?.label || "this section"}
          </Link>
        </div>
      </div>

      {/* Filters */}
      {(categories.length > 0 || data.items.length > 0) && (
        <div className="wofy-card !p-4 mb-6" data-testid="arts-filters">
          <div className="flex items-center gap-2 mb-2 text-[11px] font-mono uppercase tracking-wider opacity-70">
            <Filter className="w-3 h-3" /> Filter
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setFilter("category", "")}
              data-testid="arts-cat-all"
              className={`text-xs px-3 py-1 rounded-full border ${!category ? "bg-black text-white" : ""}`}
              style={!category ? undefined : { borderColor: "var(--wofy-border)" }}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setFilter("category", c)}
                data-testid={`arts-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-xs px-3 py-1 rounded-full border ${category === c ? "bg-black text-white" : "hover:bg-black/5"}`}
                style={category === c ? undefined : { borderColor: "var(--wofy-border)" }}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && <div className="text-sm opacity-70">Loading…</div>}

      {!loading && data.items.length === 0 && (
        <div className="wofy-card !p-6 text-center opacity-80" data-testid="arts-empty">
          <p className="mb-3">No profiles yet in this section{category ? ` (${category})` : ""}.</p>
          <Link to={`/arts/submit?section=${section}${category ? `&category=${encodeURIComponent(category)}` : ""}`}
                className="wofy-btn-primary">
            <PenSquare className="w-4 h-4" /> Be the first to add one
          </Link>
        </div>
      )}

      {!loading && data.items.length > 0 && (
        <>
          <div className="text-xs opacity-70 mb-3">{data.total} result{data.total === 1 ? "" : "s"}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="arts-entity-grid">
            {data.items.map((it) => (
              <Link
                key={it.slug}
                to={`/arts/${it.section}/${it.slug}`}
                data-testid={`arts-entity-${it.slug}`}
                className="group rounded-xl border p-4 hover:bg-black/5 transition"
                style={{ borderColor: "var(--wofy-border)" }}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="font-medium leading-tight group-hover:underline">{it.name}</div>
                  {it.is_featured && (
                    <Star className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--wofy-primary)" }} />
                  )}
                </div>
                <div className="text-[11px] font-mono uppercase tracking-wider opacity-60 mb-1">
                  {[it.category, it.country].filter(Boolean).join(" · ")}
                </div>
                {it.tagline && <p className="text-xs opacity-75 line-clamp-2">{it.tagline}</p>}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
